// src/components/ProductGalleryClient.tsx
"use client";

import { useState, useEffect } from "react";
import Image from 'next/image';
import ExpirationDateButton from './ExpirationDateButton';

import HalalBadge from './HalalBadge'
import { halalBrands } from '@/lib/halalBrands'

interface ImageNode {
    sourceUrl: string;
    altText?: string;
}

interface Props {
    productName: string;
    mainImage: string;
    mainImageAlt: string;
    gallery: ImageNode[];
    sku?: string;
    discountPercent?: number;
    inStock?: boolean;
    brandName?: string
}

export default function ProductGalleryClient({
    productName,
    mainImage,
    mainImageAlt,
    gallery,
    sku,
    discountPercent = 0,
    inStock = true,
    brandName = '',
}: Props) {
    const images = [
        { sourceUrl: mainImage, altText: mainImageAlt },
        ...gallery
    ];

    const [activeIndex, setActiveIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showAdviceButton, setShowAdviceButton] = useState(false);

    useEffect(() => {
        const checkAdviceSection = () => {
            const adviceSection = document.getElementById('product-advice-section');
            const gridElement = adviceSection?.querySelector('.grid');
            const hasAdviceCards = adviceSection && gridElement && gridElement.children.length > 0;

            if (hasAdviceCards) {
                setShowAdviceButton(true);
            }
        };

        const timer = setTimeout(checkAdviceSection, 2000);

        return () => clearTimeout(timer);
    }, []);

    const scrollToAdvice = () => {
        const adviceSection = document.getElementById('product-advice-section');
        if (adviceSection) {
            const elementPosition = adviceSection.offsetTop;
            const offsetPosition = elementPosition - 80;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    const handleThumbClick = (index: number) => {
        if (index !== activeIndex) {
            setIsTransitioning(true);
            setTimeout(() => {
                setActiveIndex(index);
                setIsTransitioning(false);
            }, 500);
        }
    };

    const currentImage = images[activeIndex];

    return (
        <div className="gallery_product_block">
            <style jsx>{`
                @keyframes slideInFade {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>

            <div className="pb-[20px] current_image_block" style={{ position: "relative", overflow: "hidden" }}>

                {/* БЕЙДЖ СКИДКИ */}
                {discountPercent > 0 && inStock && (
                    <div
                        className="absolute top-0 left-0 bg-[#ff7900] text-white font-bold px-3 py-1 text-sm z-10"
                        style={{ borderRadius: '10px 0 10px 0' }}
                    >
                        -{discountPercent}%
                    </div>
                )}

                {/* ХАЛЯЛЬ ЗНАЧОК */}
                {brandName && halalBrands[brandName] && (
                    <HalalBadge
                        brandName={brandName}
                        certificates={halalBrands[brandName].certificates}
                    />
                )}

                {/* <Image
                    src={currentImage.sourceUrl}
                    alt={currentImage.altText || productName}
                    className={`fade-image ${isTransitioning ? "transitioning" : ""}`}
                    style={{
                        display: "block",
                        width: "100%",
                        height: "auto",
                    }}
                    width={300}
                    height={300}
                /> */}

                <Image
                    src={currentImage.sourceUrl}
                    alt={currentImage.altText || productName}
                    width={600}
                    height={600}
                    sizes="(max-width: 768px) 400px, 600px"
                    className={`fade-image ${isTransitioning ? "transitioning" : ""}`}
                    style={{
                        display: "block",
                        width: "100%",
                        height: "auto",
                    }}
                />

                {/* КНОПКА СОВЕТОВ */}
                {showAdviceButton && (
                    <button
                        onClick={scrollToAdvice}
                        style={{
                            position: 'absolute',
                            bottom: '15px',
                            right: '10px',
                            backgroundColor: '#EBFFA380',
                            color: 'black',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '5px 10px',
                            fontSize: '14px',
                            fontWeight: '300',
                            cursor: 'pointer',
                            zIndex: 10,
                            boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                            transition: 'all 0.1s ease',
                            backdropFilter: 'blur(8px)',
                            opacity: 0,
                            transform: 'translateY(-10px)',
                            animation: 'slideInFade 0.3s ease-out 0.1s forwards'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#EBFFA3';
                            e.currentTarget.style.transform = 'translateY(-12px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#EBFFA380';
                            e.currentTarget.style.transform = 'translateY(-10px)';
                        }}
                    >
                        Как принимать?
                    </button>
                )}

                {/* КНОПКА СРОКА ГОДНОСТИ */}
                {sku && (
                    <ExpirationDateButton
                        sku={sku}
                        productName={productName}
                        productImage={mainImage}
                    />
                )}
            </div>

            <div className="gallery_list">
                {images.map((img, idx) => (
                    <Image
                        key={idx}
                        src={img.sourceUrl}
                        alt={img.altText || productName}
                        className={idx === activeIndex ? "active_gallery_product_item" : ""}
                        onClick={() => handleThumbClick(idx)}
                        style={{ cursor: "pointer" }}
                        width={600}
                        height={600}
                    />
                ))}
            </div>
        </div>
    );
}